import type { AppID } from '../types/db.ts';

/** What a super admin fills in when creating a tenant. Names are raw input and may contain blanks. */
export type TenantCreateDraft = {
  tenantName: string;
  groups: { groupName: string; facilityNames: string[] }[];
  appIDs: AppID[];
};

/** The cleaned-up tenant, groups, and facilities that will actually be created. */
export type TenantCreatePlan = TenantCreateDraft;

/**
 * Turns a draft into what will be created, or the reasons it can't be. Blank facility rows and groups left entirely
 * blank are ignored. When no groups remain, a single group named after the tenant is created with no facilities.
 */
export function tenantCreatePlan(
  draft: TenantCreateDraft,
): { ok: true; plan: TenantCreatePlan } | { ok: false; errors: string[] } {
  const errors: string[] = [];

  const tenantName = draft.tenantName.trim();
  if (tenantName === '') {
    errors.push('The tenant needs a name.');
  }

  const groups = draft.groups
    .map((g) => ({
      groupName: g.groupName.trim(),
      facilityNames: g.facilityNames.map((f) => f.trim()).filter((f) => f !== ''),
    }))
    .filter((g) => g.groupName !== '' || g.facilityNames.length > 0);

  const groupNamesSeen = new Set<string>();
  const facilityNamesSeen = new Set<string>();
  for (const group of groups) {
    if (group.groupName === '') {
      errors.push(`A group containing ${group.facilityNames.join(', ')} needs a name.`);
      continue;
    }

    const groupKey = group.groupName.toLowerCase();
    if (groupNamesSeen.has(groupKey)) {
      errors.push(`The group "${group.groupName}" is listed more than once.`);
    }
    groupNamesSeen.add(groupKey);

    for (const facilityName of group.facilityNames) {
      const facilityKey = facilityName.toLowerCase();
      if (facilityNamesSeen.has(facilityKey)) {
        errors.push(`The facility "${facilityName}" is listed more than once.`);
      }
      facilityNamesSeen.add(facilityKey);
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    plan: {
      tenantName,
      groups: groups.length > 0 ? groups : [{ groupName: tenantName, facilityNames: [] }],
      appIDs: Array.from(new Set(draft.appIDs)),
    },
  };
}
